import { FieldErrors, FormProvider, FormState, useForm, UseFormProps, UseFormReset } from 'react-hook-form';
import { CommanderFormContextValues, CommanderFormProvider } from '@mews-commander/ui';
import { lt, showErrorAlert } from '@mews/framework';

export interface InjectedFormProps {
    submit: () => Promise<void>;
}

export type HocSubmitHandler<V extends object> = (
    values: V,
    extra: {
        dirtyFields: FormState<V>['dirtyFields'];
        reset: UseFormReset<V>;
    }
) => Promise<void> | void;

interface WithSubmitHandler<V extends object> {
    onSubmit: HocSubmitHandler<V>;
}

export type { UseFormProps };

type SubmitFailHandler<V extends object> = (errors: FieldErrors<V>) => void;

interface OwnFormProps<V extends object> extends WithSubmitHandler<V> {
    useFormProps: UseFormProps<V>;
    onSubmitFail?: SubmitFailHandler<V>;
}

export type CombinedProps<V extends object, P> = Omit<P, keyof InjectedFormProps> &
    OwnFormProps<V> &
    Partial<CommanderFormContextValues>;

/**
 * @deprecated Please use `Form` component or `useFormControls` hook from `@mews-commander/form`.
 */
export const withHookForm =
    <V extends object, P>() =>
    (WrappedComponent: React.ComponentType<P & InjectedFormProps>) => {
        const Form = (props: CombinedProps<V, P>) => {
            const { useFormProps, onSubmit, onSubmitFail, readOnlyForm, ...other } = props;

            const formContext = useForm<V>(useFormProps);

            const {
                formState: { dirtyFields },
                reset,
            } = formContext;

            const handleInvalidSubmit = (errors: FieldErrors<V>) => {
                if (onSubmitFail) {
                    onSubmitFail(errors);
                } else {
                    showErrorAlert({ description: lt('ValidationErrorMessage') });
                }
            };

            const submit = async () =>
                formContext.handleSubmit(
                    async (values) =>
                        onSubmit(values, {
                            dirtyFields,
                            reset,
                        }),
                    handleInvalidSubmit
                )();

            const wrappedComponentProps = {
                ...(other as unknown as P),
                submit,
            };

            return (
                <CommanderFormProvider readOnlyForm={readOnlyForm}>
                    <FormProvider {...formContext}>
                        <WrappedComponent {...wrappedComponentProps} />
                    </FormProvider>
                </CommanderFormProvider>
            );
        };

        return Form;
    };
